/** @jsx jsx */

import { forwardRef } from 'react';
import { jsx } from '@balance-web/core';
import type { FlexProps } from '@balance-web/flex';
import { Flex } from '@balance-web/flex';
import { Text } from '@balance-web/text';
import { useTheme } from '@balance-web/theme';
import type { DisallowStyleProps } from '@balance-web/utils';

import { IndicatorDot } from './IndicatorDot';
import type { BadgeToneColors, IndicatorToneColors } from './useToneColor';

export type WeightType = 'bold' | 'subtle' | 'text';
export type BadgeSize = 'xsmall' | 'small' | 'medium';

type ValidBoxProps = Pick<FlexProps, 'data' | 'id'>;
export type BadgeProps = {
  /** The label of the badge. */
  label: string;
  /** The size of the badge. */
  size?: BadgeSize;
  /** The tone of the badge. */
  tone?: BadgeToneColors;
  /** The visual "weight" of the badge. */
  weight?: WeightType;
} & DisallowStyleProps &
  ValidBoxProps;

/** A decorative indicator for communicating non-actionable, supplemental information. */
export const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  (
    // NOTE: we unfortunately must spread props because designs assign tooltips to non-interactive elements like this
    {
      data,
      id,
      label,
      size = 'medium',
      tone = 'neutral',
      weight = 'subtle',
      ...tooltipProps
    },
    forwardedRef
  ) => {
    const { components, sizing } = useTheme();

    const isBold = weight === 'bold';

    const hasIndicator = !isBold;
    const hasBorder = weight === 'subtle';

    const badgeStyles = isBold
      ? { backgroundColor: components.badge.bold[tone].surfaceColor }
      : {};

    return (
      <Flex
        ref={forwardedRef}
        inline
        background={weight === 'text' ? undefined : 'base'}
        border={hasBorder ? 'standard' : undefined}
        borderRadius="full"
        data={data}
        gap="small"
        height={size === 'xsmall' ? 20 : sizing.xsmall}
        paddingX="medium"
        maxWidth="stretch"
        id={id}
        title={label}
        // flex styles
        alignItems="center"
        // TODO: remove custom styles
        css={badgeStyles}
        {...tooltipProps}
      >
        {hasIndicator ? (
          <IndicatorDot
            size={size}
            tone={validateToneIsIndicatorFriendly(tone)}
          />
        ) : null}
        <Text
          inline
          color={isBold ? 'textPrimary' : 'textBody'}
          leading="tight"
          overflowStrategy="truncate"
          // TODO: remove leading (after capsize implementation)
          size={size === 'medium' ? 'small' : 'xsmall'}
          // TODO: remove custom styles
          weight={isBold ? 'semibold' : 'medium'}
        >
          {label}
        </Text>
      </Flex>
    );
  }
);

function validateToneIsIndicatorFriendly(
  tone: BadgeToneColors
): IndicatorToneColors {
  if (
    tone === 'critical-inverse' ||
    tone === 'neutral-inverse' ||
    tone === 'primary-inverse' ||
    tone === 'secondary' ||
    tone === 'secondary-inverse'
  ) {
    throw Error(`Indicator cannot have tone ${tone}`);
  }

  return tone;
}
